import type { Copy } from "../types";

export const ko: Copy = {
    seo: {
      title: "차량 나이 계산기 | 연도와 월로 차량 연식 확인",
      description:
        "제조 연도와 선택한 월로 차량의 정확한 나이를 확인하세요. 연, 월, 일, 총 일수와 주요 시점을 한 화면에서 볼 수 있습니다.",
      keywords: [
        "차량 나이 계산기",
        "차 연식 계산기",
        "차량 제조 연도 확인",
        "차량 제조 월 확인",
        "내 차는 몇 년식",
        "차량 나이 확인",
        "차량 연식 체크",
        "RC 연식 계산기",
        "차량 나이 월 기준",
        "차량 날짜 계산기",
        "mParivahan 대안",
        "Parivahan 대안",
        "RTO 차량 연식 계산기",
        "차량 등록 연식",
        "차 등록 연식",
      ],
      faq: [
        {
          question: "이 계산기는 무엇을 하나요?",
          answer:
            "선택한 제조 연도와 선택 월을 기준으로 차량의 정확한 나이를 계산하고 연, 월, 일, 주요 시점을 보여줍니다.",
        },
        {
          question: "왜 선택 월이 있나요?",
          answer:
            "월을 추가하면 더 정확한 나이를 계산할 수 있습니다. 월을 선택하지 않으면 선택한 연도의 1월을 사용합니다.",
        },
        {
          question: "미래의 연도나 월을 선택할 수 있나요?",
          answer:
            "아니요. 미래 연도는 차단되며, 선택한 연도가 현재 연도일 때는 미래 월도 비활성화됩니다.",
        },
      ],
    },
    headerTagline: "차량 유틸리티",
    headerTitle: "차량 나이 계산기",
    reset: "초기화",
    switchToLight: "라이트 모드로 전환",
    switchToDark: "다크 모드로 전환",
    languageLabel: "언어",
    languageEnglish: "영어",
    languageHindi: "힌디어",
    heroEyebrow: "차량 연식을 한눈에",
    heroTitle: "차량 나이를 확인하는 깔끔한 날짜 도구.",
    heroDescription:
      "제조 연도를 선택하세요. 더 정확한 결과가 필요할 때만 월을 추가하세요.",
    yearSelectLabel: "제조 연도",
    monthSelectLabel: "제조 월",
    includeMonthLabel: "정확도를 높이기 위해 특정 월 포함",
    includeMonthNote: "월을 포함하지 않으면 계산에는 1월이 사용됩니다.",
    yearOnlyBadge: "연도만",
    yearMonthBadge: "연도 + 월",
    yearPlaceholder: "연도 선택",
    monthPlaceholder: "월 선택",
    inputTitle: "제조 연도",
    inputLabel: "제조 연도",
    blockedDateNote: "미래 연도는 선택할 수 없습니다.",
    result: "결과",
    vehicleAgeLead: "차량은",
    asOfToday: "오늘 기준",
    years: "년",
    months: "개월",
    days: "일",
    totalDays: (count) => `총 ${count.toLocaleString("en-IN")}일`,
    ageLabels: { almostNew: "거의 새 차", modern: "현대적", mature: "성숙", classic: "클래식" },
    estimatedValue: {
      high: "높은 재판매 가능성",
      healthy: "가치 유지가 좋은 편",
      moderate: "보통 수준의 감가",
      collector: "수집가 관심이 높아질 수 있음",
    },
    maintenanceTitle: "정비",
    maintenance: {
      routine: "정기 점검과 순정 관리",
      watch: "브레이크, 오일, 타이어를 점검하세요",
      plan: "소모품과 점검 일정을 계획하세요",
      specialized: "전문 정비의 중요성이 커집니다",
    },
    registrationTitle: "등록",
    registration: {
      collector: "일부 지역에서는 수집가용 취급이 가능할 수 있습니다",
      classic: "일부 지역에서는 클래식카 등록이 적용될 수 있습니다",
      standard: "일반 갱신과 지역 점검을 예상하세요",
      regular: "대체로 일반 등록이 적용됩니다",
    },
    upcomingMilestoneTitle: "다가오는 주요 시점",
    milestoneTimelineTitle: "주요 시점 타임라인",
    milestoneRemaining: ({ label, days }) => `${label}까지 ${days.toLocaleString("ko-KR")}일`,
    reached: "달성",
    upcoming: "예정",
    allMilestonesReachedTitle: "표시된 모든 주요 시점을 이미 달성했습니다",
    allMilestonesReachedBody:
      "이 차량은 앱에 표시된 주요 시점 범위를 이미 넘었습니다.",
    copySummary: "요약 복사",
    shareResult: "결과 공유",
    resetMessage: "계산기를 초기화했습니다",
    summaryCopied: "요약이 클립보드에 복사되었습니다",
    clipboardFailed: "클립보드에 접근하지 못했습니다",
    emptyTitle: "제조 연도를 선택하면 상세 정보가 표시됩니다",
    emptyDescription:
      "연도를 선택하면 정확한 나이, 총 일수, 정비 메모, 클래식 상태, 주요 시점이 표시됩니다.",
    shareTitle: "차량 나이 계산기",
    summary: ({ label, years, months, days, asOf }) =>
      `제 차량은 ${label}에 제조되었고 ${asOf} 기준으로 ${years}년 ${months}개월 ${days}일입니다.`,
    milestones: {
      1: { label: "1년", note: "보증과 정비 계획에 유용합니다." },
      3: { label: "3년", note: "재판매와 가치 변화에서 자주 보이는 시점입니다." },
      5: { label: "5년", note: "정비 예산을 다시 점검하기 좋은 때입니다." },
      10: { label: "10년", note: "노후 부품에 더 많은 주의가 필요합니다." },
      15: { label: "15년", note: "많은 지역에서 클래식 취급이 시작될 수 있습니다." },
      25: { label: "25년", note: "일부 시장에서는 수집가 및 수입용으로 유리합니다." },
    },
  };
